import React, { Component, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Route } from 'react-router';
import { ThemeProvider } from 'styled-components';
import {
  Switch,
  withRouter,
} from 'react-router-dom';
import queryString from 'query-string';

import {
  getStorage,
  setStorage,
  PrivateRoute,
} from '@minerva-analytics/pantheon';

import Header from 'containers/Header';
import Alerts from 'containers/Alerts';
import ScrollToTop from 'components/ScrollToTop';
import Modals from 'containers/Modals';
import Footer from 'components/Footer';

import extraActions from 'store/extra/actions';
import userActions from 'store/user/actions';
import authedUser from 'store/user/selectors/authedUser';
import alertActions from 'store/alert/actions';

import { CLOUDFRONT_URL, getLastElement } from 'utils';
import { trackUser, trackPageView } from 'utils/event';
import routes from 'root/routes';
import theme from 'root/theme';

import { AppContainer } from './App.styles';
import GlobalStyle from '../../index.styles';

/**
 * The App view wraps all other views in the react application, providing a
 * consistent look and feel across all views
 */
export const EventRoute = (props) => {
  useEffect(() => {
    trackPageView();
  }, [props.location.pathname]);

  return <Route { ...props } />;
};

export class App extends Component {
  componentDidMount() {
    const {
      authedUserId,
      user,
      history: { location },
      createAlert,
    } = this.props;
    const parsed = queryString.parse(location.search);

    if (authedUserId) {
      trackUser(authedUserId.toString());
      if (!user.email_confirmed && user.confirmation_email_queued) {
        createAlert({
          type: `warning`,
          message: `You haven't confirmed your email yet. Please check ${user.email}`,
          time: 5000,
        });
      }
    }

    if (parsed.wall) {
      setStorage({ key: `wall`, value: parsed.wall, session: true, forceCookies: true });
    }

    if (parsed.quiz_wall) {
      setStorage({ key: `quiz_wall`, value: parsed.quiz_wall, session: true, forceCookies: true });
    }

    if (parsed.g_button) {
      setStorage({ key: `g_button`, value: parsed.g_button, session: true, forceCookies: true });
    }

    if (parsed.pk_source) {
      setStorage({ key: `pk_source`, value: parsed.pk_source, forceCookies: true });
    }

    if (parsed.pk_medium) {
      setStorage({ key: `pk_medium`, value: parsed.pk_medium, forceCookies: true });
    }

    this.handleReferral();
  }

  getReferralUsername = () => {
    const { location } = this.props;

    const referralParamUsername = queryString.parse(location.search).ref;
    const referralUrlUsername = location.pathname.includes(`referral`)
      ? getLastElement(location.pathname.split(`/`)) !== `referral` // handles `/referral` case
        ? getLastElement(location.pathname.split(`/`))
        : ``
      : null;
    const referralStorageUsername = getStorage({ key: `referral-token`, session: true });
    if (typeof referralParamUsername === `string` || typeof referralUrlUsername === `string` || typeof referralStorageUsername === `string`) {
      // The order for which referralUser name to use is,
      // from the url path -> from the url param -> from sessionStorage
      return typeof referralUrlUsername === `string`
        ? referralUrlUsername
        : typeof referralParamUsername === `string`
          ? referralParamUsername
          : referralStorageUsername;
    }

    return null;
  }

  handleReferral = async () => {
    const {
      setReferralToken,
      jwt,
      validateReferral,
    } = this.props;

    const referrerUsername = this.getReferralUsername();
    if (referrerUsername) {
      const validatedUsername = await setReferralToken(referrerUsername);
      if (validatedUsername && jwt) {
        validateReferral();
      }
    }
  }

  render() {
    const { jwt } = this.props;
    const isAuthenticated = Boolean(jwt);
    return (
      <ThemeProvider theme={ theme }>
        <AppContainer>
          <GlobalStyle />
          <Alerts { ...this.props } />
          <Modals />
          <Header isAuthenticated={ isAuthenticated } />
          <Helmet>
            <title> Quizhive Trivia - Best Trivia with Friends </title>
            <meta property="og:image" content={ `${CLOUDFRONT_URL}/qh_main_sharing.jpeg` } />
            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=0" />
            <meta name="google-site-verification" content="a1XZHo9scrkMi8cunWX3fzGoJdGS3SwY3pnpu2YG9SY" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content="Quizhive Trivia - Best Trivia With Friends" />
            <meta name="description" property="og:description" content="Quizhive is the best way to test your knowledge on different trivia. You can also see how you perform against people from around the world." />
            <meta property="fb:app_id" content="589253514514371" />
            <meta name="twitter:site" content="@quizhive_" />
          </Helmet>
          <ScrollToTop />
          <Switch>
            {routes.map((route) => (
              route.private
                ? <PrivateRoute
                  isAuthenticated={ isAuthenticated }
                  key={ route.path }
                  { ...route }
                />
                : <EventRoute
                  key={ route.path }
                  { ...route }
                />
            ))}
          </Switch>
          <Footer />
        </AppContainer>
      </ThemeProvider>
    );
  }
}

App.propTypes = {
  jwt: PropTypes.string.isRequired,
  location: PropTypes.object,
  loadToken: PropTypes.func.isRequired,
  loadSession: PropTypes.func.isRequired,
  saveQueryParams: PropTypes.func.isRequired,
  setReferralToken: PropTypes.func.isRequired,
  validateReferral: PropTypes.func.isRequired,
  setABLeg: PropTypes.func,
  authedUserId: PropTypes.number,
  createAlert: PropTypes.func,
  user: PropTypes.object,
  history: PropTypes.object,
};


export default withRouter(connect((state) => {
  return {
    jwt: state.user.JWT,
    authedUserId: state.user.session.authedUserId,
    user: authedUser(state),
  };
}, {
  saveQueryParams: extraActions.saveQueryParams,
  loadToken: userActions.loadToken,
  loadSession: userActions.loadSession,
  checkExpiredToken: userActions.checkExpiredToken,
  validateReferral: userActions.validateReferral,
  setReferralToken: extraActions.setReferralToken,
  setABLeg: extraActions.setABLeg,
  createAlert: alertActions.createAlert,
})(App));
